import React, { useState, useEffect } from 'react';
import { Mail, Server, User, Key, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface EmailConfig {
  smtpServer: string;
  smtpPort: number;
  account: string;
  authCode: string;
  senderName: string;
}

const defaultConfig: EmailConfig = {
  smtpServer: 'smtp.exmail.qq.com',
  smtpPort: 465,
  account: '',
  authCode: '',
  senderName: '编程优题库',
};

export default function EmailSettings() {
  const [config, setConfig] = useState<EmailConfig>(defaultConfig);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    const { data } = await supabase.from('email_settings').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle();
    if (data) {
      setConfig({
        smtpServer: data.smtp_server || defaultConfig.smtpServer,
        smtpPort: data.smtp_port || defaultConfig.smtpPort,
        account: data.account || '',
        authCode: data.auth_code || '',
        senderName: data.sender_name || defaultConfig.senderName,
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from('email_settings').upsert({
      smtp_server: config.smtpServer,
      smtp_port: config.smtpPort,
      account: config.account,
      auth_code: config.authCode,
      sender_name: config.senderName,
      updated_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) {
      setMessage({ type: 'error', text: '保存失败' });
    } else {
      setMessage({ type: 'success', text: '配置保存成功' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleTest = () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: '请输入测试邮箱' });
      return;
    }
    setTesting(true);
    setTimeout(() => {
      setTesting(false);
      setMessage({ type: 'success', text: `测试邮件已发送至 ${testEmail}` });
      setTimeout(() => setMessage(null), 3000);
    }, 1500);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          邮件服务器配置
        </h2>

        {message && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SMTP服务器</label>
            <div className="relative">
              <Server className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={config.smtpServer}
                onChange={(e) => setConfig({ ...config, smtpServer: e.target.value })}
                className="w-full pl-9 pr-3 py-2 border rounded-lg"
                placeholder="如: smtp.exmail.qq.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">端口</label>
            <input
              type="number"
              value={config.smtpPort}
              onChange={(e) => setConfig({ ...config, smtpPort: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">发件账号</label>
            <div className="relative">
              <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={config.account}
                onChange={(e) => setConfig({ ...config, account: e.target.value })}
                className="w-full pl-9 pr-3 py-2 border rounded-lg"
                placeholder="如: service@udo100.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">授权码/密码</label>
            <div className="relative">
              <Key className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={config.authCode}
                onChange={(e) => setConfig({ ...config, authCode: e.target.value })}
                className="w-full pl-9 pr-3 py-2 border rounded-lg"
                placeholder="邮箱授权码"
              />
            </div>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">发件人名称</label>
            <input
              type="text"
              value={config.senderName}
              onChange={(e) => setConfig({ ...config, senderName: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="如: 编程优题库"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            {saving ? '保存中...' : '保存配置'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Send className="w-5 h-5 text-green-600" />
          发送测试邮件
        </h3>
        <div className="flex items-center gap-4">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-lg"
            placeholder="请输入测试邮箱地址"
          />
          <button
            onClick={handleTest}
            disabled={testing}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {testing ? '发送中...' : '发送测试'}
          </button>
        </div>
      </div>
    </div>
  );
}
